<script setup lang="ts">
import { h, ref } from 'vue';
import { AkModal, AkInput, AkHeading4, AkButton, AkIcon } from '@ankorstore/design-system';
import { toRefs, watchEffect } from 'vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { nextTick } from 'vue';
import icons from './icons';
import { getContent } from './templates';
import { AksInsertImageButton, MAX_IMAGE_WIDTH, PLUGIN_KEY } from './config';
import { EditorInteractionManager, MediaLibraryServiceComposable } from '../types';
import ImageDrawerBanner from './image-drawer-banner.vue';
import { CaptureErrorFunc } from '../../use-error-logger';

enum ModalMode {
  Insert = 'insert',
  Edit = 'edit',
  Closed = 'closed',
}

const DEFAULT_IMAGE_WIDTH = 300;

defineOptions({ name: 'AksInsertImagePlugin' });

const props = defineProps<{
  id: string;
  editorInteractionManager: EditorInteractionManager;
  useMediaLibraryService: MediaLibraryServiceComposable;
  captureError: CaptureErrorFunc;
}>();

const emit = defineEmits(['toolbarButtonClicked', 'contentInserted']);

const { id, editorInteractionManager: im } = toRefs(props);
const modal = ref<typeof AkModal>(null);
const { t } = useI18n();

const isAspectRatioLocked = ref(true);
const widthInput = ref<typeof AkInput>(null);
const selectedNode = ref<HTMLElement | null>(null);
const modalMode = ref<ModalMode>(ModalMode.Closed);

const replaceContent = (node: HTMLElement, content: string) => {
  im.value.selectNode(node);
  im.value.deleteNode();
  im.value.insertContent(content);
};

const { openMediaLibraryDrawer, closeMediaLibraryDrawer } = props.useMediaLibraryService({
  mode: 'select',
  eventHandlers: {
    onItemClick: (item) => {
      const backgroundImage = new Image();
      backgroundImage.src = item.attributes.url;

      backgroundImage.onload = () => {
        // Load the image manually first to access the aspect ratio
        const aspectRatio = backgroundImage.naturalWidth / backgroundImage.naturalHeight;

        const content = getContent({
          src: item.attributes.url,
          pluginKey: PLUGIN_KEY,
          width: DEFAULT_IMAGE_WIDTH,
          height: Math.round(DEFAULT_IMAGE_WIDTH / aspectRatio),
        });

        if (modalMode.value === ModalMode.Edit) {
          replaceContent(selectedNode.value, content);
        } else {
          im.value.insertContent(content);
          emit('contentInserted', PLUGIN_KEY);
        }

        if (!im.value.queryCommandState('JustifyCenter')) {
          im.value.justifyCenter();
        }

        modalMode.value = ModalMode.Closed;
        closeMediaLibraryDrawer();
      };
    },
  },
  slots: {
    header: () => h(ImageDrawerBanner),
  },
});

const aspectRatio = ref<number>(1);
const width = ref(250);
const height = ref(250);
const href = ref('');
const errors = ref<{
  width: string;
  height: string;
  href: string;
}>({
  width: '',
  height: '',
  href: '',
});

const notices = ref<{ width: string }>({ width: '' });

const pluginId = computed(() => id.value + '_' + PLUGIN_KEY);
const validateForm = () => {
  let isValid = true;

  if (!width.value || width.value <= 0) {
    errors.value.width = t('account.brands.createCampaigns.editor.toolbar.insertImage.modal.error.required');
    isValid = false;
  } else if (width.value > MAX_IMAGE_WIDTH) {
    if (isAspectRatioLocked.value) {
      height.value = Math.round(MAX_IMAGE_WIDTH / aspectRatio.value);
    }
    width.value = MAX_IMAGE_WIDTH;
    notices.value.width = t('account.brands.createCampaigns.editor.toolbar.insertImage.modal.notice.widthAdjusted');
  }

  if (!height.value || height.value <= 0) {
    errors.value.height = t('account.brands.createCampaigns.editor.toolbar.insertImage.modal.error.required');
    isValid = false;
  }

  if (href.value && !href.value.startsWith('https://')) {
    errors.value.href = t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.url.error.wrongPrefix');
    isValid = false;
  }

  return isValid;
};

const clearForm = () => {
  width.value = 0;
  height.value = 0;
  href.value = '';
  aspectRatio.value = 1;

  clearErrors('width');
  clearErrors('height');
  clearErrors('href');
  notices.value.width = '';
};

const register = () => {
  im.value.registerPlugin(pluginId.value, ({ addButton, addContextToolbar, addIcons }) => {
    addIcons(icons);

    addButton(AksInsertImageButton.InsertImage, {
      text: t('account.brands.createCampaigns.editor.toolbar.insertImage.label'),
      icon: 'image',
      onAction: () => {
        modalMode.value = ModalMode.Insert;
        openMediaLibraryDrawer();
        emit('toolbarButtonClicked', PLUGIN_KEY);
      },
    });

    addButton(AksInsertImageButton.ReselectImage, {
      icon: 'image',
      onAction: () => {
        if (selectedNode.value) {
          modalMode.value = ModalMode.Edit;
          openMediaLibraryDrawer();
        }
      },
    });

    addButton(AksInsertImageButton.EditImage, {
      icon: 'edit-block',
      onAction: () => {
        if (selectedNode.value) {
          const properties = JSON.parse(selectedNode.value.dataset.aksProperties || '{}');

          width.value = parseInt(properties.width ?? '250', 10);
          height.value = parseInt(properties.height ?? '250', 10);
          href.value = selectedNode.value.getAttribute('href');

          const image = new Image();
          image.src = properties.src;

          image.onload = () => {
            aspectRatio.value = image.width / image.height;
          };

          openEditModal({ mode: ModalMode.Edit });
        }
      },
    });

    addButton(AksInsertImageButton.RemoveImage, {
      icon: 'remove',
      onAction: () => {
        if (selectedNode.value) {
          im.value.selectNode(selectedNode.value);
          im.value.deleteNodeContent();
        }
      },
    });

    addContextToolbar('imageselection', {
      predicate: (node) => {
        if (node?.hasAttribute(`data-${PLUGIN_KEY}`)) {
          selectedNode.value = node as HTMLElement;
          im.value.selectNode(selectedNode.value);
          return true;
        }

        return false;
      },
      items: [AksInsertImageButton.EditImage, AksInsertImageButton.ReselectImage, AksInsertImageButton.RemoveImage].join(' '),
      position: 'node',
      scope: 'node',
    });
  });
};

const handleSubmitEdit = () => {
  const hadNotice = !!notices.value.width;

  if (!validateForm() && im.value) {
    return;
  }

  // A freshly raised clamp notice keeps the modal open so the adjusted width stays visible.
  if (notices.value.width && !hadNotice) {
    return;
  }

  const properties = JSON.parse(selectedNode.value.dataset.aksProperties || '{}');

  const content = getContent({
    src: properties.src,
    height: height.value,
    width: width.value,
    href: href.value,
    pluginKey: PLUGIN_KEY,
  });

  replaceContent(selectedNode.value, content);
  closeEditModal();
};

const openEditModal = async (options: { mode?: ModalMode }) => {
  modalMode.value = options.mode ?? ModalMode.Insert;
  modal.value.openModal();
  await nextTick();
  widthInput.value?.focusInput();
};

const closeEditModal = () => {
  modalMode.value = ModalMode.Closed;
  modal.value.close();
  clearForm();
};

const clearErrors = (field: 'width' | 'height' | 'href') => {
  errors.value[field] = '';
};

const handleInput = (field: 'width' | 'height') => {
  clearErrors(field);
  // Editing either field can recompute the width, so the clamp notice is always stale here.
  notices.value.width = '';

  if (isAspectRatioLocked.value) {
    if (field === 'width') {
      height.value = Math.round(width.value / aspectRatio.value);
    } else {
      width.value = Math.round(height.value * aspectRatio.value);
    }
  }
};

const toggleAspectRatioLock = () => {
  isAspectRatioLocked.value = !isAspectRatioLocked.value;
};

watchEffect(() => {
  if (modal.value && im.value && pluginId.value) {
    register();
  }
});
</script>
<template>
  <Teleport to="body">
    <AkModal
      ref="modal"
      data-testid="insert-image-plugin"
      @cancelled="closeEditModal"
    >
      <div class="insert-image-modal__content">
        <AkHeading4 class="insert-image-modal__title">
          {{ t('account.brands.createCampaigns.editor.toolbar.insertImage.modal.title') }}
        </AkHeading4>
        <form
          class="insert-image-modal__form"
          @submit.prevent="handleSubmitEdit"
        >
          <div class="insert-image-modal__input-row">
            <AkInput
              ref="widthInput"
              v-model="width"
              :placeholder="t('account.brands.createCampaigns.editor.toolbar.insertImage.modal.width.placeholder')"
              :label="t('account.brands.createCampaigns.editor.toolbar.insertImage.modal.width.label')"
              class="insert-image-modal__input"
              type="number"
              :errors="errors.width"
              data-testid="width-input"
              @input="handleInput('width')"
            />
            <AkIcon
              :symbol="isAspectRatioLocked ? 'lock' : 'unlock'"
              size="lg"
              data-testid="aspect-ratio-lock-icon"
              @click="toggleAspectRatioLock"
            />
            <AkInput
              v-model="height"
              :placeholder="t('account.brands.createCampaigns.editor.toolbar.insertImage.modal.height.placeholder')"
              :label="t('account.brands.createCampaigns.editor.toolbar.insertImage.modal.height.label')"
              class="insert-image-modal__input"
              type="number"
              :errors="errors.height"
              data-testid="height-input"
              @input="handleInput('height')"
            />
          </div>
          <p
            v-if="notices.width"
            class="insert-image-modal__notice"
            data-testid="widthNotice"
          >
            {{ notices.width }}
          </p>
          <AkInput
            v-model="href"
            :placeholder="t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.url.placeholder')"
            :label="t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.url.label')"
            class="insert-image-modal__input insert-image-modal__input--full-width"
            :errors="errors.href"
            data-testid="href-input"
            @input="clearErrors('href')"
          />
          <div class="insert-image-modal__actions">
            <AkButton
              outlined
              @click="closeEditModal"
            >
              {{ t('account.brands.createCampaigns.editor.toolbar.insertImage.modal.cancel') }}
            </AkButton>
            <AkButton
              type="submit"
              @click.prevent="handleSubmitEdit"
            >
              {{ t('account.brands.createCampaigns.editor.toolbar.insertImage.modal.submit') }}
            </AkButton>
          </div>
        </form>
      </div>
    </AkModal>
  </Teleport>
</template>

<style scoped lang="scss">
.insert-image-modal__content {
  @apply ds-text-left;

  .insert-image-modal__title {
    @apply ds-mb-4 ds-text-center;
  }

  .insert-image-modal__description {
    @apply ds-mb-4 ds-text-center;
  }

  .insert-image-modal__form {
    @apply ds-flex ds-gap-3 ds-flex-wrap;
  }

  .insert-image-modal__input-row {
    @apply ds-flex ds-gap-4 ds-items-center;

    i {
      @apply ds-cursor-pointer;
    }
  }

  .insert-image-modal__input {
    @apply ds-mb-4;

    &--full-width {
      @apply ds-flex-1;
    }
  }

  .insert-image-modal__notice {
    @apply ds-w-full ds-text-sm ds-text-info-700;
  }

  .insert-image-modal__actions {
    @apply ds-w-full ds-flex ds-justify-center ds-gap-4;
  }
}
</style>
